import { bytesToHex, fromBase64Url, hexToBytes, toBase64Url, utf8 } from './bytes.ts';
import { sha256Hex } from './canon.ts';
import { listAccountRecords, lookupCcdScan, lookupWalletProxy, type Fetch } from './chain.ts';
import {
  bindingHash,
  bindingKeyFor,
  bindingRecordDiffs,
  decodeNetIdRecord,
  hostIsBound,
  keyFingerprint,
  parseDnsTxt,
  type BindingDocument,
  type BindingRecord,
} from './binding.ts';
import type { CompanyBlock, IssuerDocument } from './issuer.ts';
import type { NetIdPacket } from './packet.ts';
import { APPROVED_COMPANY_IDPS, COMPANY_ATTRIBUTES } from './proof-request.ts';
import type { ProofVerification } from './proof-verify.ts';

/**
 * The company checks of SPEC.md §8.5, run on the phone: who is behind the
 * code (the Concordium identity proof), which domains and keys that company
 * bound on chain, and whether this code's page and key are among them.
 *
 * Until the company has made its proof and sent its binding, every check that
 * needs them is PENDING ("pending onboarding"), never green.
 */
export type CompanyCheckKey =
  | 'identity'
  | 'challenge'
  | 'attributes'
  | 'binding'
  | 'sender'
  | 'validity'
  | 'revocation'
  | 'key'
  | 'domain'
  | 'dns'
  | 'binding-second-source';

export type CheckState = 'ok' | 'fail' | 'pending';

export type CompanyCheck = { key: CompanyCheckKey; label: string; state: CheckState; required: boolean; line: string; detail: string };

export type CompanyVerification = {
  status: 'verified' | 'pending' | 'failed';
  checks: CompanyCheck[];
  /** Exactly as the identity provider verified them; null unless the proof verified. */
  legalName: string | null;
  businessNumber: string | null;
  legalCountry: string | null;
  /** Shown when the proof revealed it (onboarding asks for it). */
  lei: string | null;
  idpName: string | null;
  account: string | null;
  page: string | null;
  proofMethod: string | null;
  bindingTx: string | null;
  summary: string;
};

/** What the scanner's trust bundle may expect of an issuer (optional). */
export type CompanyExpectation = {
  account?: string;
  legalName?: string;
  businessNumber?: string;
  legalCountry?: string;
};

export type CompanyOptions = {
  issuerDoc: IssuerDocument;
  packet: NetIdPacket;
  /** The public key that verified the packet's signature. */
  signingKey: Uint8Array;
  fetch: Fetch;
  /** Verifies the identity proof (proof-verify.ts, lazily loaded in the browser). */
  verifyProof: (company: CompanyBlock) => Promise<ProofVerification>;
  expect?: CompanyExpectation;
  now?: number;
  /** DNS-over-HTTPS JSON endpoint; null skips the optional DNS check. */
  doh?: string | null;
  /** TESTS ONLY: approve other identity providers (default: APPROVED_COMPANY_IDPS). */
  approvedIdps?: Record<number, string>;
};

export const DEFAULT_DOH = 'https://cloudflare-dns.com/dns-query';

const LABELS: Record<CompanyCheckKey, string> = {
  identity: 'Company identity proof',
  challenge: 'Proof made for this binding',
  attributes: 'Company details',
  binding: 'Binding registered on Concordium',
  sender: 'Sent by the company account',
  validity: 'Binding is valid now',
  revocation: 'Binding not revoked',
  key: "Code signed with a bound key",
  domain: 'Page on a bound domain',
  dns: 'DNS agrees',
  'binding-second-source': 'Second chain source agrees',
};

const OPTIONAL: CompanyCheckKey[] = ['dns', 'binding-second-source'];

export async function verifyCompany(o: CompanyOptions): Promise<CompanyVerification> {
  const now = o.now ?? Date.now();
  const checks: CompanyCheck[] = [];
  const set = (key: CompanyCheckKey, state: CheckState, line: string, detail = line) => {
    checks.push({ key, label: LABELS[key], state, required: !OPTIONAL.includes(key), line, detail });
  };
  const company = o.issuerDoc.company;
  const p = o.packet;
  const out: CompanyVerification = {
    status: 'pending',
    checks,
    legalName: null,
    businessNumber: null,
    legalCountry: null,
    lei: null,
    idpName: null,
    account: company?.account ?? p.company ?? null,
    page: p.page ?? null,
    proofMethod: null,
    bindingTx: company?.binding_tx ?? null,
    summary: '',
  };
  const finish = (): CompanyVerification => {
    const req = checks.filter((c) => c.required);
    out.status = req.some((c) => c.state === 'fail') ? 'failed' : req.some((c) => c.state === 'pending') || !req.length ? 'pending' : 'verified';
    out.summary =
      out.status === 'verified'
        ? `${out.legalName} · ${out.businessNumber} · ${out.legalCountry}`
        : out.status === 'pending'
          ? 'Company identity: pending onboarding'
          : `Company identity failed: ${req.filter((c) => c.state === 'fail').map((c) => c.label.toLowerCase()).join(', ')}`;
    return out;
  };

  if (p.netid !== 2 && p.netid !== 3) {
    set('identity', 'pending', 'This code is an older format without a company identity.');
    return finish();
  }
  if (!company) {
    for (const k of ['identity', 'binding', 'key', 'domain'] as CompanyCheckKey[]) set(k, 'pending', `${o.issuerDoc.issuer} has not published its company identity yet (pending onboarding).`);
    return finish();
  }

  // 0. The binding document itself, and the accounts that must agree.
  const doc: BindingDocument = company.binding;
  let docHash: string;
  try {
    docHash = bindingHash(doc).sha256;
    if (docHash !== company.binding_sha256) throw new Error(`the published binding hashes to ${docHash.slice(0, 12)}…, not ${company.binding_sha256.slice(0, 12)}…`);
  } catch (e) {
    set('binding', 'fail', 'The published binding document is invalid.', (e as Error).message);
    return finish();
  }
  const accountProblems: string[] = [];
  if (doc.company.account !== company.account) accountProblems.push(`binding names ${doc.company.account}, the company block ${company.account}`);
  if (p.company !== company.account) accountProblems.push(`the code names account ${p.company}, the issuer's company account is ${company.account}`);
  if (o.expect?.account && o.expect.account !== company.account) accountProblems.push(`the app expects ${o.issuerDoc.issuer}'s company account to be ${o.expect.account}, not ${company.account}`);
  if (doc.company.idp !== company.idp) accountProblems.push('binding and company block name different identity providers');
  const proofSha = company.proof ? sha256Hex(utf8(company.proof.json)) : null;

  // 1–3. The identity proof.
  if (!company.proof) {
    set('identity', 'pending', 'The company has not made its identity proof yet (pending onboarding).');
  } else {
    let pv: ProofVerification;
    try {
      pv = await o.verifyProof(company);
    } catch (e) {
      pv = { ok: false, method: 'none', methodLabel: 'the verifier could not run', claims: null, idpName: null, error: (e as Error).message };
    }
    out.proofMethod = pv.methodLabel;
    const approved = o.approvedIdps ?? APPROVED_COMPANY_IDPS[doc.company.network];
    const idp = pv.claims?.issuerIdp ?? null;
    if (!pv.ok) {
      set('identity', 'fail', 'The company identity proof does not verify.', `${pv.methodLabel}: ${pv.error ?? 'failed'}`);
    } else if (idp === null || !(idp in approved)) {
      set('identity', 'fail', `The identity comes from identity provider ${idp}, not an approved company identity provider.`, `approved: ${JSON.stringify(approved)}`);
    } else if (idp !== doc.company.idp) {
      set('identity', 'fail', `The proof is from identity provider ${idp}; the binding names ${doc.company.idp}.`);
    } else if (pv.claims?.validUntil && now > Date.parse(pv.claims.validUntil)) {
      set('identity', 'fail', `The company's identity expired ${pv.claims.validUntil}.`);
    } else if (company.proof.sha256 !== proofSha) {
      set('identity', 'fail', 'The published proof hash does not match the proof.', `${company.proof.sha256} ≠ ${proofSha}`);
    } else {
      out.idpName = approved[idp] ?? pv.idpName;
      set('identity', 'ok', `Verified by ${out.idpName} via Concordium (${pv.methodLabel}).`, `${pv.methodLabel}; style ${company.proof.style}`);
    }
    if (pv.ok) {
      const ch = pv.claims?.challenge ?? null;
      if (ch === docHash) set('challenge', 'ok', 'The proof was made for this binding of domains and keys.', `challenge ${ch}`);
      else set('challenge', 'fail', 'The proof was made for something else, not this binding.', `challenge ${ch} ≠ binding ${docHash}`);
      const r = pv.claims?.revealed ?? {};
      const missing = COMPANY_ATTRIBUTES.filter((a) => !r[a]);
      const wrong: string[] = [];
      for (const a of COMPANY_ATTRIBUTES) {
        const want = o.expect?.[a];
        if (want && r[a] && r[a] !== want) wrong.push(`${a} is "${r[a]}", expected "${want}"`);
      }
      if (missing.length) set('attributes', 'fail', `The proof does not reveal: ${missing.join(', ')}.`);
      else if (wrong.length) set('attributes', 'fail', `The company details are not the expected ones: ${wrong.join('; ')}.`);
      else {
        out.legalName = r.legalName;
        out.businessNumber = r.businessNumber;
        out.legalCountry = r.legalCountry;
        out.lei = r.lei ?? null;
        set('attributes', 'ok', `${r.legalName} · business number ${r.businessNumber} · ${r.legalCountry}, as the identity provider verified them.`);
      }
    }
  }

  // 4–7. The binding record on chain.
  let record: BindingRecord | null = null;
  if (!company.binding_tx) {
    if (accountProblems.length) set('sender', 'fail', 'The accounts do not agree.', accountProblems.join('; '));
    set('binding', 'pending', 'The company has not registered its binding on Concordium yet (pending onboarding).');
  } else {
    const net = doc.company.network;
    try {
      const tx = await lookupCcdScan(net, company.binding_tx, o.fetch);
      if (!tx.success) throw new Error('the binding transaction failed on chain');
      if (!tx.dataHex) throw new Error('the binding transaction registers no data');
      const rec = decodeNetIdRecord(hexToBytes(tx.dataHex));
      if (rec.type !== 'binding') throw new Error('the transaction holds a revocation, not a binding');
      record = rec;
      const diffs = bindingRecordDiffs(rec, doc, proofSha);
      if (!tx.finalized) set('binding', 'fail', 'The binding is on Concordium but not finalized yet.');
      else if (diffs.length) set('binding', 'fail', `The binding on chain does not match: ${diffs[0]}.`, diffs.join('; '));
      else set('binding', 'ok', `Registered on Concordium: the hash of this binding, ${doc.domains.length} domain(s), ${doc.keys.length} key(s)${rec.p ? ' and the proof' : ''}.`, `tx ${tx.txHash} block ${tx.blockHeight} ${tx.blockTime}`);
      if (accountProblems.length) set('sender', 'fail', 'The accounts do not agree.', accountProblems.join('; '));
      else if (tx.sender !== company.account) set('sender', 'fail', `The binding was sent by ${tx.sender}, not the company account.`);
      else set('sender', 'ok', 'Sent from the company\'s own Concordium account.', tx.sender);
      try {
        const wp = await lookupWalletProxy(net, company.binding_tx, o.fetch);
        const ok = wp.finalized && wp.success && wp.sender === tx.sender && wp.dataHex === tx.dataHex;
        set('binding-second-source', ok ? 'ok' : 'fail', ok ? `${wp.source} reports the same binding.` : `${wp.source} disagrees.`);
      } catch (e) {
        set('binding-second-source', 'fail', 'A second chain source could not confirm the binding (optional).', (e as Error).message);
      }
    } catch (e) {
      set('binding', 'fail', 'The binding could not be read from Concordium.', `${company.binding_tx}: ${(e as Error).message}`);
    }
    if (record) {
      const nowS = Math.floor(now / 1000);
      if (nowS < record.notBefore) set('validity', 'fail', `The binding starts only on ${new Date(record.notBefore * 1000).toISOString().slice(0, 10)}.`);
      else if (nowS > record.notAfter) set('validity', 'fail', `The binding expired on ${new Date(record.notAfter * 1000).toISOString().slice(0, 10)}.`);
      else set('validity', 'ok', `Valid until ${new Date(record.notAfter * 1000).toISOString().slice(0, 10)}.`);
      // Revoked or superseded? Look at every record the account sent after the binding.
      try {
        const { records, reachedStop } = await listAccountRecords(net, company.account, o.fetch, { stopAt: company.binding_tx });
        const fp = bytesToHex(keyFingerprint(toBase64Url(o.signingKey)));
        let revoked: string | null = null;
        let superseded: string | null = null;
        for (const r of records) {
          if (r.txHash === company.binding_tx) break;
          let rec;
          try {
            rec = decodeNetIdRecord(hexToBytes(r.dataHex));
          } catch {
            continue; // an article record or other data
          }
          if (rec.type === 'revocation' && rec.b === docHash && (!rec.keys || rec.keys.includes(fp))) revoked ??= `${r.txHash} (${r.blockTime ?? '?'})`;
          if (rec.type === 'binding' && rec.b !== docHash) superseded ??= r.txHash;
        }
        if (revoked) set('revocation', 'fail', 'The company has REVOKED this binding on Concordium.', `revocation ${revoked}`);
        else if (superseded) set('revocation', 'fail', 'The company has registered a newer binding; this one is out of date.', `newer binding ${superseded}`);
        else if (!reachedStop) set('revocation', 'fail', 'Could not check that the binding is not revoked.', 'the binding transaction was not found in the account history');
        else set('revocation', 'ok', 'No revocation or newer binding from the company account.');
      } catch (e) {
        set('revocation', 'fail', 'Could not check that the binding is not revoked.', (e as Error).message);
      }
    }
  }

  // 8. The code's key is one the company bound.
  const kb = bindingKeyFor(doc, p.kid, o.signingKey, now);
  if ('error' in kb) set('key', 'fail', "The code's signing key is not one the company bound.", kb.error);
  else set('key', company.binding_tx ? 'ok' : 'pending', company.binding_tx ? `Signed with key ${p.kid}, bound by the company.` : `Key ${p.kid} is in the published binding; waiting for it on chain.`);

  // 9. The page is on a bound domain, and the issuer is too.
  let host = '';
  try {
    host = new URL(p.page!).hostname;
  } catch {
    /* handled below */
  }
  if (!host) set('domain', 'fail', 'The code names no valid page address.');
  else if (!hostIsBound(doc, host)) set('domain', 'fail', `This code is for ${host}, which is not one of the company's domains (${doc.domains.join(', ')}).`);
  else if (!p.customer && !hostIsBound(doc, p.issuer)) set('domain', 'fail', `The issuer ${p.issuer} is not one of the company's domains.`);
  else set('domain', company.binding_tx ? 'ok' : 'pending', `This code is for ${host}${new URL(p.page!).pathname}, a domain the company bound.`);

  // 10. Optional: DNS of the issuer domain agrees (DNS-over-HTTPS).
  if (o.doh !== null && company.binding_tx) {
    try {
      const name = `_netid.${p.customer ? host : p.issuer}`;
      const res = await o.fetch(`${o.doh ?? DEFAULT_DOH}?name=${encodeURIComponent(name)}&type=TXT`, { headers: { accept: 'application/dns-json' } });
      const j = (await res.json()) as { Answer?: Array<{ type: number; data: string }> };
      const txts = (j.Answer ?? []).filter((a) => a.type === 16).map((a) => parseDnsTxt(a.data)).filter(Boolean);
      const match = txts.find((t) => t!.acct === company.account && t!.bind === docHash);
      if (match) set('dns', 'ok', `DNS ${name} names the same account and binding.`);
      else set('dns', 'fail', txts.length ? `DNS ${name} names another account or binding.` : `No PageID record in DNS ${name} (optional).`);
    } catch (e) {
      set('dns', 'fail', 'DNS could not be checked (optional).', (e as Error).message);
    }
  }

  return finish();
}

/**
 * The company checks for a SITE rather than a scanned code (the PageID label, SPEC.md §12): the same checks
 * as `verifyCompany`, for the company's published key and a page on `host`. Used by PageID's label frame and
 * `/v/` page; the caller caches the result.
 */
export async function verifyCompanyForSite(o: Omit<CompanyOptions, 'packet' | 'signingKey'> & { host: string }): Promise<CompanyVerification> {
  const doc = o.issuerDoc;
  const now = o.now ?? Date.now();
  const k = doc.keys.find((x) => x.status !== 'revoked' && x.status !== 'retired' && (!x.not_after || Date.parse(x.not_after) > now)) ?? doc.keys[0];
  if (!k) throw new Error(`${doc.issuer} has no published key`);
  const signingKey = fromBase64Url(k.public_key);
  const packet = { netid: 3, issuer: doc.issuer, customer: doc.issuer, kind: 'page', page: `https://${o.host}/`, kid: k.kid, iat: now, nonce: 'label', ...(doc.company ? { company: doc.company.account } : {}) } as unknown as NetIdPacket;
  return verifyCompany({ ...o, packet, signingKey });
}

